import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";
import writersStudioImage from "@/assets/writers-studio-adu.jpg";

const HowToUseADUWritersStudio = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-writers-studio" />
      <Navigation />
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight" style={{ color: '#E4683B' }}>
                  Your Own Writing Sanctuary
                </h1>
                <p className="text-xl text-white leading-relaxed">
                  A private writer's studio ADU frees your creativity and improves productivity.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={openCalendlyModal}
                  >
                    📅 Book & Schedule
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={writersStudioImage} 
                  alt="Warm writer's studio ADU with desk by window and bookshelves" 
                  className="rounded-lg shadow-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Focus-Boosting Design Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              Focus-Boosting Design
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Natural Light</h3>
                  <p className="text-muted-foreground">Large windows flood the room with daylight to enhance creativity and mood.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Quiet Insulation</h3>
                  <p className="text-muted-foreground">Sound insulation blocks house noise and distractions for deep focus work.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Built-in Storage</h3>
                  <p className="text-muted-foreground">Custom bookshelves and library walls keep references and inspiration organized.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Writer-Friendly Features */}
        <section className="py-16 px-4 bg-muted/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
              Writer-Friendly Features
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Ergonomic Workspace</h3>
                <p className="text-muted-foreground">Cozy writing desk setup with daylight-adjustable lighting for all-day comfort.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Refreshment Station</h3>
                <p className="text-muted-foreground">Dedicated coffee or tea setup to fuel long writing sessions and creative flow.</p>
              </div>
            </div>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-[#E4683B] hover:bg-white/90"
                onClick={() => window.location.href = '/landing'}
              >
                📐 What fits my lot?
              </Button>
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/price-roi-report', '_blank')}
              >
                💰 Get Price & ROI Report
              </Button>
            </div>
          </div>
        </section>

        {/* Workflow & Well-Being Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              Workflow & Well-Being
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Work-Life Balance</h3>
                <p className="text-muted-foreground">Separate creative space helps establish healthy boundaries between work and home life.</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Morning Rituals</h3>
                <p className="text-muted-foreground">Dedicated space encourages consistent writing routines and productive habits.</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Multi-Use Space</h3>
                <p className="text-muted-foreground">Easily converts to guest accommodation when needed for visiting friends or family.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-4 bg-primary/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8 text-white">
              Rediscover your focus. Escape the noise. Make space for stories and stillness.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('/our-work', '_blank')}
              >
                🏡 Our Portfolio
              </Button>
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/faq', '_blank')}
              >
                ❓ Our FAQ
              </Button>
            </div>
          </div>
        </section>

        {/* Calendly Modal */}
        {showCalendly && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
              <button 
                onClick={closeCalendlyModal} 
                className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
              <iframe 
                src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
                width="100%" 
                height="100%" 
                frameBorder="0" 
                title="Schedule a meeting" 
              />
            </div>
          </div>
        )}
        
        <Footer />
        <FloatingADUWizard />
      </div>
  );
};

export default HowToUseADUWritersStudio;